/*
  HANNE BLOCH, the campaign pictures in the collection grid: the looks (HB 2026-10-20, Emilie's Resort 27 collection
  page, built from her mockups of 16 Oct: hb-resort-27-collection-preview.html, hb-added-to-cart-A.html).

  A look is a campaign picture or film between the cards (snippets/hb-look-tile, rendered by sections/main-collection
  after the card each look names, Theme settings > Collection pages: the looks). A tap on it opens "In this picture"
  in the shared slide-up (snippets/hb-sheet, window.HBSheet from assets/hb-sheet.js): the pieces worn in the picture,
  each as a small line (its 2:3 picture, its name and price, its sizes or its length) with its own quiet "Add to cart".
  An add puts that piece in the cart with /cart/add.js, as the quick add and the quick view do; the words become
  "Added to cart" with the bag the moment she presses (26 Oct, as quick as on live). A sheet with one piece slides down
  as soon as the cart has it; a sheet with several pieces stays, so she can add the next, and 2 s after the press the
  words become "Remove", which takes that piece out again (/cart/change.js, one less of that line). The cart stays closed; the bag count follows (the drawer's section and the gift's state travel with
  the request, as assets/hb-cart.js does it, and are handed to the theme in one CartUpdateEvent with the drawer's
  auto-open lifted for that dispatch). HB 2026-11-03 (team test): when the sheet slides down after an add, the quick add's
  note rises with the piece, as after the set builder (until then nothing rose over the page, Emilie, 16 Oct).
  A picture of a set carries BUILD MY SET: the sheet closes and the set builder (sections/hb-set, HBSheet.openSet)
  opens on that set, at its colour line (the first page would say what this sheet said already). Every other
  picture, one piece or more, carries the "Add to cart" under the pieces (22 Oct, Emilie): it adds every piece as
  chosen, one after the other (addAll), and reads Sold out while no piece can be added; on a sheet of one piece it
  is the only button (the piece's quiet one is hidden there, .hb-lk--one) and reads Preorder when that is what the
  chosen size is. The button is the outlined one (Emilie's pick of 22 Oct): ink frame on white, the grey with white
  words while pressed, hovered or working, "Added to cart" with the bag in the grey after (assets/hb-look.css).
  A tap on a piece's picture or name goes to its product page, the sheet's history entry going first (the quick
  view's rule, 6.15.6), so Back from the product page lands here with the sheet closed.
  Never for B2B (the snippet renders nothing for them). Styles: assets/hb-look.css.
  ?hb_look=<n> on a collection page opens look n on load (for checking).
*/
import { CartUpdateEvent } from '@theme/events';

const VERSION = '2026-11-03-looks9'; // looks9: the sheet slides down with the quick add's note, as the set builder does; looks8 was looks7 with the add as quick as on live: "Added to cart" at the press, the sheet down the moment the cart has the piece
const DESKTOP = 750;
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const GIFT_SECTION = 'hb-tgift-state';
// HB 2026-10-26 (Emilie: "the speed of adding it to the cart should match the exact one as in the current live theme. this
// also goes for all buttons created on the new collection page and in the build my set feature"). Measured on live the
// same day: the product page's button shows Horizon's "Added" 12 ms after the press, with the request still under way,
// for 2 s; the quick view and the set builder slide down the moment /cart/add.js answers (8 to 12 ms after it), and the
// note rises as they finish. Here the same: "Added to cart" with the bag at the press, all the pieces in one request,
// a sheet of one piece down the moment the cart has it; a sheet of several stays, and the words become Remove 2 s after
// the press (until 26 Oct: the words changed when the request came back, and the sheet waited 1.1 s more).
const ADDED_MS = 2000; // "Added to cart" from the press, where the sheet stays open (Horizon's own "Added" on the product page)

let H = null; // window.HBSheet (assets/hb-sheet.js)
let S = null; // its state
let SHEET_MS = 350;
const LK = { navTo: '', openedAt: 0 };

const dlg = () => document.querySelector('dialog.hb-sl');
const q = (sel, root) => (root || dlg()).querySelector(sel);
const phone = () => window.innerWidth < DESKTOP;
const SIZE_RANK = ['M', 'L', 'S', 'XL', 'XS', 'XXL', 'XXS', 'XXXL', '3XL'];
// the house bag, as the header draws it (snippets/cart-icon.liquid)
const BAG =
  '<svg class="hb-lk__bag" viewBox="0 0 502 587.18" fill="none" stroke="currentColor" stroke-width="35" aria-hidden="true"><rect x="17.5" y="226.24" width="467" height="343.44"/><path d="M251,18S153-3,130.81,226.24"/><path d="M244.82,18S342.76-3,365,226.24"/></svg>';

function track(name, data) {
  try {
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(name, data || {});
    }
  } catch (e) {
    /* analytics never breaks the sheet */
  }
  try {
    if (typeof window.clarity === 'function') window.clarity('event', name);
  } catch (e) {
    /* nothing */
  }
}

function rememberedSize() {
  try {
    return String(localStorage.getItem('hb-size') || '')
      .trim()
      .toUpperCase();
  } catch (e) {
    return '';
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Opening

function openLook(tile, opener) {
  if (!H || !S || S.open || (typeof H.blocked === 'function' && H.blocked())) return;
  const tpl = tile.querySelector('template[data-hb-look-sheet]');
  if (!tpl) return;
  const frag = tpl.content.cloneNode(true);
  const root = frag.querySelector('.hb-lk');
  if (!root) return;
  if (!H.open('look', opener || tile)) return;
  const d = dlg();
  const title = root.getAttribute('data-hb-look-title') || 'In this picture';
  d.setAttribute('aria-label', title);
  if (typeof H.setHead === 'function') H.setHead({ title });
  const body = q('[data-hb-sl-body]', d);
  const foot = q('[data-hb-sl-foot]', d);
  body.innerHTML = '';
  body.appendChild(frag);
  foot.innerHTML = '';
  LK.openedAt = performance.now();
  wire(root, tile);
  track('hb_look_open', { look: tile.getAttribute('data-hb-look') || '' });
}

// ---------------------------------------------------------------------------------------------------------------
// The pieces: their variants, the size rows, the add

function varsOf(piece) {
  if (piece.__hbVars) return piece.__hbVars;
  let list = [];
  try {
    const el = piece.querySelector('[data-hb-look-vars]');
    list = el ? JSON.parse(el.textContent) : [];
  } catch (e) {
    list = [];
  }
  piece.__hbVars = Array.isArray(list) ? list : [];
  return piece.__hbVars;
}

// the values chosen on each axis, in order
function picks(piece) {
  return [...piece.querySelectorAll('[data-hb-look-axis]')].map((row) => {
    const on = row.querySelector('.hb-lk__pill.is-on');
    return on ? on.getAttribute('data-hb-look-pick') || '' : '';
  });
}

function variantOf(piece) {
  const vars = varsOf(piece);
  const rows = piece.querySelectorAll('[data-hb-look-axis]');
  if (!rows.length) return vars[0] || null;
  const key = picks(piece).join('/');
  return vars.find((v) => v.k === key) || null;
}

// a value is in stock on its axis when a variant with it (and the choices before it) is available
function inStock(piece, axis, value) {
  const vars = varsOf(piece);
  const chosen = picks(piece);
  return vars.some((v) => {
    if (!v.a) return false;
    const parts = String(v.k).split('/');
    if (parts[axis] !== value) return false;
    for (let i = 0; i < axis; i++) if (chosen[i] && parts[i] !== chosen[i]) return false;
    return true;
  });
}

// her first choice: M, else the nearest in stock (the site's rule, hb-website-building 6.4.6); Regular for a length
function firstPick(piece, axis, values) {
  const pref = rememberedSize();
  const ok = (v) => inStock(piece, axis, v);
  if (values.some((v) => /^regular$/i.test(v))) {
    const reg = values.find((v) => /^regular$/i.test(v));
    if (ok(reg)) return reg;
  }
  if (pref && values.some((v) => v.toUpperCase() === pref && ok(v))) return values.find((v) => v.toUpperCase() === pref);
  for (const r of SIZE_RANK) {
    const hit = values.find((v) => v.toUpperCase() === r && ok(v));
    if (hit) return hit;
  }
  return values.find(ok) || values[0] || '';
}

function paintPiece(piece) {
  const rows = [...piece.querySelectorAll('[data-hb-look-axis]')];
  rows.forEach((row, axis) => {
    row.querySelectorAll('.hb-lk__pill').forEach((p) => {
      const v = p.getAttribute('data-hb-look-pick') || '';
      p.classList.toggle('is-out', !inStock(piece, axis, v));
    });
  });
  const v = variantOf(piece);
  const price = piece.querySelector('[data-hb-look-price]');
  // the shop's money format wraps the amount in a span and our own Liquid wrote these (as assets/hb-qv.js takes them)
  if (price && v) price.innerHTML = (v.c ? '<s>' + v.c + '</s> ' : '') + v.p;
  const add = piece.querySelector('[data-hb-look-add]');
  if (add && !add.classList.contains('is-done') && !add.classList.contains('is-rm')) {
    if (!v || !v.a) {
      add.textContent = 'Sold out';
      add.disabled = true;
    } else {
      add.textContent = v.pre ? 'Preorder' : 'Add to cart';
      add.disabled = false;
    }
  }
  syncAll(piece.closest('.hb-lk'));
}

// the "Add to cart" under a picture without a set (22 Oct, Emilie: under every such sheet, a picture of one piece
// too): it reads Sold out and does nothing while no piece can be added as chosen (every piece sold out). On a
// sheet of one piece it is the only button (the piece's own quiet one is hidden, assets/hb-look.css .hb-lk--one),
// so it also reads Preorder when that piece's chosen size is a preorder.
function syncAll(root) {
  const btn = root ? root.querySelector('[data-hb-look-addall]') : null;
  if (!btn || btn.classList.contains('is-busy') || btn.classList.contains('is-done')) return;
  if (!btn.dataset.words) btn.dataset.words = btn.innerHTML;
  const pieces = [...root.querySelectorAll('[data-hb-look-piece]')];
  const live = (add) => add && !(add.disabled && !add.classList.contains('is-done') && !add.classList.contains('is-rm'));
  const dead = pieces.every((piece) => !live(piece.querySelector('[data-hb-look-add]')));
  btn.disabled = dead;
  btn.classList.toggle('is-out', dead);
  let words = btn.dataset.words;
  if (dead) words = '<span>Sold out</span>';
  else if (pieces.length === 1) {
    const add = pieces[0].querySelector('[data-hb-look-add]');
    if (add && !add.classList.contains('is-done') && !add.classList.contains('is-rm') && /^preorder$/i.test(add.textContent.trim())) words = '<span>Preorder</span>';
  }
  btn.innerHTML = words;
}

function setupPiece(piece) {
  const rows = [...piece.querySelectorAll('[data-hb-look-axis]')];
  rows.forEach((row, axis) => {
    const pills = [...row.querySelectorAll('.hb-lk__pill')];
    const values = pills.map((p) => p.getAttribute('data-hb-look-pick') || '');
    const first = firstPick(piece, axis, values);
    pills.forEach((p) => p.classList.toggle('is-on', (p.getAttribute('data-hb-look-pick') || '') === first));
    pills.forEach((p) => {
      p.addEventListener('click', () => {
        if (piece.querySelector('[data-hb-look-add].is-rm')) return; // the piece is in the cart as chosen: Remove first
        pills.forEach((x) => x.classList.toggle('is-on', x === p));
        // a change on the first axis: the next axis picks again within what is in stock
        for (let i = axis + 1; i < rows.length; i++) {
          const nxt = [...rows[i].querySelectorAll('.hb-lk__pill')];
          const vals = nxt.map((x) => x.getAttribute('data-hb-look-pick') || '');
          const on = nxt.find((x) => x.classList.contains('is-on'));
          const cur = on ? on.getAttribute('data-hb-look-pick') || '' : '';
          if (!cur || !inStock(piece, i, cur)) {
            const f = firstPick(piece, i, vals);
            nxt.forEach((x) => x.classList.toggle('is-on', (x.getAttribute('data-hb-look-pick') || '') === f));
          }
        }
        paintPiece(piece);
      });
    });
  });
  paintPiece(piece);
}

// ---------------------------------------------------------------------------------------------------------------
// The cart

const drawerSectionId = () => {
  const el = document.querySelector('cart-drawer-component cart-items-component');
  return el ? el.dataset.sectionId || '' : '';
};
const giftOnPage = () => !!document.getElementById('hb-tg-data');

async function cartPost(path, body) {
  const payload = Object.assign({}, body);
  const id = drawerSectionId();
  if (id) {
    payload.sections = giftOnPage() ? id + ',' + GIFT_SECTION : id;
    payload.sections_url = window.location.pathname;
  }
  const res = await fetch(ROOT + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.status) {
    const err = new Error(data.description || data.message || path + ' failed');
    err.data = data;
    throw err;
  }
  return data;
}

function countFrom(data) {
  const id = drawerSectionId();
  const html = data && data.sections && id && data.sections[id];
  if (html) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const n = doc.querySelector('[ref="cartItemCount"]');
      if (n && n.textContent.trim() !== '') return parseInt(n.textContent, 10) || 0;
    } catch (e) {
      /* the cart's own count then */
    }
  }
  return typeof (data && data.item_count) === 'number' ? data.item_count : undefined;
}

// The new cart to the theme (the drawer's lines, the bag count, the gift's state), the drawer kept closed: it opens on
// every cart:update while it carries auto-open (assets/cart-drawer.js), so the attribute is lifted for this dispatch,
// as assets/hb-cart.js does for the quick add.
function publish(data) {
  const root = document.querySelector('cart-drawer-component');
  const had = root && root.hasAttribute('auto-open') ? root.getAttribute('auto-open') : null;
  if (root && had !== null) root.removeAttribute('auto-open');
  try {
    document.dispatchEvent(
      new CartUpdateEvent({ source: 'hb-look' }, 'hb-look', { source: 'hb-look', itemCount: countFrom(data), sections: data && data.sections })
    );
  } finally {
    if (root && had !== null) setTimeout(() => root.setAttribute('auto-open', had || ''), 0);
  }
}

// the words of an add, at the press: the grey, "Added to cart" and the bag (assets/hb-look.css, is-done)
function shownAdded(el) {
  el.classList.add('is-done');
  el.innerHTML = '<span>Added to cart</span>' + BAG;
}

function itemOf(piece) {
  const v = variantOf(piece);
  if (!v || !v.a) return null;
  const item = { id: Number(v.id), quantity: 1 };
  if (v.pre && v.note) item.properties = { Preorder: String(v.note) };
  return item;
}

function klaviyo(data) {
  try {
    if (typeof window.triggerKlaviyoAddedToCart === 'function') window.triggerKlaviyoAddedToCart(data);
  } catch (e) {
    /* nothing */
  }
}

// the line the cart made for a piece: its key and quantity, for Remove
function keep(piece, line) {
  piece.dataset.lineKey = (line && line.key) || '';
  piece.dataset.lineQty = String((line && line.quantity) || 1);
}

const isSingle = (root) => root.querySelectorAll('[data-hb-look-piece]').length === 1 && !root.querySelector('[data-hb-look-set]');

// HB 2026-11-03 (team test: "after pressing ADD TO CART, should it fold down and show the small success message? It
// does this correctly after the set builder"): the sheet slides down and the quick add's note rises with the piece
// (assets/hb-cart.js reads HBCart.state.qaPiece on a cart:update from 'variant-popup-inner'), exactly as the set builder
// and the steps builder do it (assets/hb-sheet.js, assets/hb-steps.js). Until now nothing rose over the page here
// (Emilie, 16 Oct). Several pieces added at once are named together ("A and B", "A and 2 more pieces").
function closeWithNote(adds) {
  const pieces = (adds || []).map((t) => t && t.piece).filter(Boolean);
  const titles = pieces.map((p) => p.dataset.title || '').filter(Boolean);
  let name = titles[0] || '';
  if (titles.length === 2) name = titles[0] + ' and ' + titles[1];
  else if (titles.length > 2) name = titles[0] + ' and ' + (titles.length - 1) + ' more pieces';
  const img = pieces[0] ? pieces[0].dataset.img || '' : '';
  try {
    if (window.HBCart && window.HBCart.state) window.HBCart.state.qaPiece = { name, img, at: performance.now() };
  } catch (e) {
    /* the note then rises without the piece */
  }
  if (S && S.open && S.mode === 'look') H.close();
  setTimeout(() => {
    document.dispatchEvent(new CartUpdateEvent({ source: 'variant-popup-inner' }));
  }, SHEET_MS + 20);
}

// after an add: a sheet of one piece, and the "Add to cart" that adds every piece of the picture, slide down with the
// note (closeWithNote); a piece's own quiet add on a sheet of several keeps the sheet open, so she can add the next,
// and its words become Remove (and the foot's button gets its words back) ADDED_MS after the press
function settle(root, adds, t0, btn, words) {
  if (isSingle(root) || btn) {
    closeWithNote(adds);
    return;
  }
  setTimeout(() => {
    if (!root.isConnected) return;
    adds.forEach(({ piece, add }) => {
      if (!add || !piece.dataset.lineKey) return;
      add.classList.remove('is-done');
      add.classList.add('is-rm');
      add.textContent = 'Remove';
    });
    if (btn) {
      btn.classList.remove('is-done');
      btn.innerHTML = words;
    }
  }, Math.max(0, ADDED_MS - (performance.now() - t0)));
}

function notAdded(add, was) {
  add.classList.remove('is-done', 'is-busy');
  add.textContent = 'Not added, try again';
  setTimeout(() => {
    if (!add.classList.contains('is-done') && !add.classList.contains('is-rm')) add.innerHTML = was;
  }, 2500);
}

async function addPiece(root, piece, add) {
  if (add.disabled || add.classList.contains('is-busy') || add.classList.contains('is-done')) return;
  const item = itemOf(piece);
  if (!item) return;
  const t0 = performance.now();
  const was = add.innerHTML;
  shownAdded(add);
  try {
    const data = await cartPost('cart/add.js', { items: [item] });
    klaviyo(data);
    keep(piece, data && Array.isArray(data.items) ? data.items[0] : data);
    track('hb_look_add', { handle: piece.dataset.handle || '', variant: item.id });
    publish(data);
    settle(root, [{ piece, add }], t0);
  } catch (e) {
    notAdded(add, was);
  }
}

async function removePiece(piece, add) {
  if (add.classList.contains('is-busy')) return;
  const key = piece.dataset.lineKey;
  const qty = Math.max(0, (parseInt(piece.dataset.lineQty || '1', 10) || 1) - 1);
  if (!key) return;
  add.classList.add('is-busy');
  try {
    const data = await cartPost('cart/change.js', { id: key, quantity: qty });
    piece.dataset.lineKey = '';
    piece.dataset.lineQty = '';
    track('hb_look_remove', { handle: piece.dataset.handle || '' });
    add.classList.remove('is-busy', 'is-rm');
    publish(data);
    paintPiece(piece);
  } catch (e) {
    add.classList.remove('is-busy');
  }
}

// ---------------------------------------------------------------------------------------------------------------
// BUILD MY SET: this sheet closes, the builder opens on its set at the colour line

function toSet(key, opener) {
  if (!H || typeof H.openSet !== 'function') return;
  const want = key || 'silk-tulle';
  track('hb_look_set', { set: want });
  H.close();
  // the sheet's close finishes (SHEET_MS) before the builder's open, so the closing timer never wipes the builder.
  // HB 2026-10-21 (Emilie's option 5): the Resort 27 set opens in the steps builder (assets/hb-steps.js) when it is on
  // the page; the other sets keep the image-first builder (HBSheet.openSet)
  setTimeout(() => {
    if (S && S.open) return;
    const steps = window.HBSteps;
    if (steps && typeof steps.open === 'function' && typeof steps.handles === 'function' && steps.handles(want)) {
      steps.open(want, opener || null);
      return;
    }
    H.openSet(opener || null, want, { start: true });
  }, SHEET_MS + 80);
}

// ---------------------------------------------------------------------------------------------------------------
// Wiring, the ways out

function wire(root, tile) {
  root.querySelectorAll('[data-hb-look-piece]').forEach((piece) => {
    setupPiece(piece);
    const add = piece.querySelector('[data-hb-look-add]');
    if (add) {
      add.addEventListener('click', () => {
        if (add.classList.contains('is-rm')) removePiece(piece, add);
        else addPiece(root, piece, add);
      });
    }
    // the piece's picture or name: to its page, the sheet's history entry going first (Back then lands here)
    piece.querySelectorAll('a.hb-lk__th, a.hb-lk__name').forEach((a) => {
      a.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        track('hb_look_to_product', { handle: piece.dataset.handle || '' });
        if (!S || !S.hist) {
          location.href = a.href;
          return;
        }
        LK.navTo = a.href;
        H.close();
      });
    });
  });
  const set = root.querySelector('[data-hb-look-set]');
  if (set) {
    set.addEventListener('click', () => {
      set.classList.add('is-busy');
      toSet(set.getAttribute('data-hb-look-set') || '', tile);
    });
  }
  // HB 2026-10-22 (Emilie): a picture without a set has the "Add to cart" at the foot, every piece as chosen, one after
  // the other
  const all = root.querySelector('[data-hb-look-addall]');
  if (all) all.addEventListener('click', () => addAll(root, all));
}

// The outlined button (Emilie's pick, 22 Oct): pressed it fills with the grey (assets/hb-look.css, :active and
// is-pressed) and reads "Added to cart" with the bag at once (is-done, 26 Oct); every piece as chosen goes in with one
// request, as the set builder adds a set; a sheet of one piece slides down the moment the cart has it, a sheet of
// several gets its words back ADDED_MS after the press. If the cart refuses the request (a piece just sold out), the
// pieces go one by one, so the others still go in.
async function addAll(root, btn) {
  if (btn.classList.contains('is-busy') || btn.classList.contains('is-done')) return;
  const pieces = [...root.querySelectorAll('[data-hb-look-piece]')];
  const todo = pieces
    .map((piece) => ({ piece, add: piece.querySelector('[data-hb-look-add]'), item: itemOf(piece) }))
    .filter(({ add, item }) => item && add && !add.disabled && !add.classList.contains('is-rm') && !add.classList.contains('is-done') && !add.classList.contains('is-busy'));
  if (!todo.length) return;
  const t0 = performance.now();
  const words = btn.innerHTML;
  shownAdded(btn);
  todo.forEach((t) => {
    t.was = t.add.innerHTML;
    shownAdded(t.add);
  });
  let added = [];
  try {
    const data = await cartPost('cart/add.js', { items: todo.map((t) => t.item) });
    klaviyo(data);
    const lines = data && Array.isArray(data.items) ? data.items : [];
    todo.forEach((t) => {
      const line = lines.find((l) => Number(l.variant_id || l.id) === t.item.id);
      if (line) {
        keep(t.piece, line);
        added.push(t);
      }
    });
    publish(data);
  } catch (e) {
    added = [];
    for (const t of todo) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const one = await cartPost('cart/add.js', { items: [t.item] });
        klaviyo(one);
        keep(t.piece, one && Array.isArray(one.items) ? one.items[0] : one);
        added.push(t);
        publish(one);
      } catch (e2) {
        notAdded(t.add, t.was);
      }
    }
  }
  track('hb_look_add_all', { pieces: added.length });
  if (!added.length) {
    btn.classList.remove('is-done');
    btn.innerHTML = '<span>Not added, try again</span>';
    setTimeout(() => {
      if (root.isConnected && !btn.classList.contains('is-done')) btn.innerHTML = words;
    }, 2500);
    return;
  }
  settle(root, added, t0, btn, words);
}

function onPop() {
  if (!LK.navTo) return;
  const u = LK.navTo;
  LK.navTo = '';
  location.href = u;
}

function onTileTap(e) {
  const t = e.target instanceof Element ? e.target.closest('[data-hb-look-open]') : null;
  if (!t || t.closest('dialog.hb-sl')) return;
  const tile = t.closest('[data-hb-look]');
  if (!tile) return;
  e.preventDefault();
  openLook(tile, t);
}

let tries = 0;
function init() {
  H = window.HBSheet;
  const d = dlg();
  if (!H || !d || !H.state || typeof H.open !== 'function') {
    // assets/hb-sheet.js is a module too: it may land a moment after this one
    if (tries++ < 40) setTimeout(init, 250);
    return;
  }
  if (H.look) return; // wired already
  S = H.state;
  SHEET_MS = Number(H.SHEET_MS) || 350;
  H.look = { version: VERSION, open: openLook };
  // the sheet's own close keeps is-qv and is-set in mind, not this mode: the class goes when the dialog has closed
  d.addEventListener('close', () => d.classList.remove('is-look'));
  // the press shows at once (22 Oct, Emilie: "the button reacts a bit too slow"): the grey goes on at pointerdown, before
  // the click and the request, and comes off when the finger or the button lifts (the click then keeps it with is-busy);
  // the empty touchstart listener is for iOS, which applies :active only when something listens for a touch
  d.addEventListener('pointerdown', (e) => {
    const b = e.target instanceof Element ? e.target.closest('.hb-lk__set') : null;
    if (b && !b.disabled) b.classList.add('is-pressed');
  });
  // the pressed look leaves a frame after the finger lifts, so a tap's click (is-busy) takes over without a flicker
  const unpress = () => d.querySelectorAll('.hb-lk__set.is-pressed').forEach((b) => b.classList.remove('is-pressed'));
  const release = () => {
    requestAnimationFrame(unpress);
    setTimeout(unpress, 120); // a page without frames for a moment (hidden, or iOS mid-gesture) still lets go
  };
  d.addEventListener('pointerup', release);
  d.addEventListener('pointercancel', release);
  d.addEventListener('touchstart', () => {}, { passive: true });
  document.addEventListener('click', onTileTap);
  window.addEventListener('popstate', onPop);
  // ?hb_look=<n>: that look's sheet on load (for checking)
  try {
    const n = new URLSearchParams(location.search).get('hb_look');
    if (n) {
      const tile = document.querySelector('[data-hb-look="' + n.replace(/[^0-9]/g, '') + '"]');
      if (tile) setTimeout(() => openLook(tile, tile.querySelector('[data-hb-look-open]')), 700);
    }
  } catch (e) {
    /* nothing */
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
